/**
 * メール送信予約 (mail_schedules) の hooks。
 * 仕様書: docs/01_spec/spec_mail_system.md B-12
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import type {
  MailSchedule,
  MailScheduleStatus,
  MailScheduleTargetType,
} from '@goenhub/shared';

import { db, functions } from '@/lib/firebase';

export interface MailScheduleRecord extends MailSchedule {
  id: string;
}

interface UseMailSchedulesOptions {
  status?: MailScheduleStatus | MailScheduleStatus[];
  mailId?: string;
}

export function useMailSchedules(options?: UseMailSchedulesOptions) {
  return useQuery({
    queryKey: ['mail_schedules', options ?? null],
    queryFn: async (): Promise<MailScheduleRecord[]> => {
      const conditions = [];
      if (options?.mailId) {
        conditions.push(where('mailId', '==', options.mailId));
      }
      if (Array.isArray(options?.status)) {
        if (options.status.length > 0) {
          conditions.push(where('status', 'in', options.status));
        }
      } else if (typeof options?.status === 'string') {
        conditions.push(where('status', '==', options.status));
      }

      const snap = await getDocs(
        query(
          collection(db, 'mail_schedules'),
          ...conditions,
          orderBy('sendAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as MailSchedule),
      }));
    },
  });
}

interface SchedulePayload {
  mailId: string;
  targetType: MailScheduleTargetType;
  targetIds: string[];
  /** Phase 26: 送信元アカウント ID (mail_accounts/{id}) */
  fromAccountId: string;
  ctaUrl?: string;
  /** CC 宛先メアド（任意）。1 通ごとに付与される */
  cc?: string[];
  /** ISO 文字列 */
  sendAt: string;
}

const scheduleMailSendFn = httpsCallable<
  SchedulePayload,
  { ok: boolean; scheduleId: string; recipientCount: number }
>(functions, 'scheduleMailSend');

export function useScheduleMailSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SchedulePayload) => {
      const res = await scheduleMailSendFn(payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_schedules'] }),
  });
}

const cancelScheduledMailFn = httpsCallable<
  { scheduleId: string },
  { ok: boolean }
>(functions, 'cancelScheduledMail');

export function useCancelScheduledMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const res = await cancelScheduledMailFn({ scheduleId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_schedules'] }),
  });
}

const deleteScheduledMailFn = httpsCallable<
  { scheduleId: string },
  { ok: boolean; alreadyGone?: boolean }
>(functions, 'deleteScheduledMail');

/** 予約レコードを Firestore から物理削除（リストの掃除用）。 */
export function useDeleteScheduledMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const res = await deleteScheduledMailFn({ scheduleId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_schedules'] }),
  });
}

// Phase 26: useAuthorizedGmailAddress は廃止。送信元一覧は useMailAccounts に移行。
